import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { Plus, Edit2, Trash2, Image as ImageIcon, Camera, X, MapPin, Gauge, Calendar } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { formatApiError } from "../lib/errors";
import { compressImage, uploadImage } from "../lib/image";

const MAX_IMAGES = 20;

function formatRideDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
}

function fileToGalleryUrl(file) {
    return uploadImage(file, api, "gallery").catch(() => compressImage(file)).catch(() => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    });
}

const empty = {
    ride_name: "",
    location: "",
    total_kms: "",
    ride_date: "",
    images: [],
    order: 0,
    active: true,
};

const inputCls = "w-full bg-black border border-meraki-gold/20 px-4 py-3 text-meraki-text focus:outline-none focus:border-meraki-gold";

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">{label}</label>
            {children}
            {hint && <div className="text-[10px] text-meraki-muted/70 mt-1.5">{hint}</div>}
        </div>
    );
}

function GalleryImagesEditor({ value, onChange, testid }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const images = value || [];

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        const room = MAX_IMAGES - images.length;
        if (room <= 0) {
            toast.error(`Max ${MAX_IMAGES} photos per ride.`);
            return;
        }
        const batch = files.slice(0, room);
        if (files.length > room) toast.message(`Only ${room} more photo(s) allowed — extra files skipped.`);
        setUploading(true);
        const uploaded = [];
        for (const file of batch) {
            if (file.size > 8 * 1024 * 1024) {
                toast.error(`${file.name} is too large (max 8MB). Skipped.`);
                continue;
            }
            try {
                uploaded.push({ url: await fileToGalleryUrl(file), caption: "" });
            } catch {
                toast.error(`Couldn't process ${file.name}.`);
            }
        }
        if (uploaded.length) onChange([...images, ...uploaded]);
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const setCaption = (i, caption) => onChange(images.map((img, j) => (j === i ? { ...img, caption } : img)));

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                    {images.length} / {MAX_IMAGES} photos
                </div>
                {uploading && <div className="text-[10px] uppercase tracking-[0.25em] text-meraki-gold animate-pulse">Uploading…</div>}
            </div>
            {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {images.map((img, i) => (
                        <div key={`${img.url}-${i}`} className="relative bg-black border border-meraki-gold/20 overflow-hidden group">
                            <div className="relative aspect-square">
                                <img src={img.url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => onChange(images.filter((_, j) => j !== i))}
                                    className="absolute top-1 right-1 bg-black/80 border border-meraki-gold/40 p-1 text-meraki-gold hover:text-red-400 hover:border-red-400 transition-colors"
                                    aria-label="Remove photo"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                                {i === 0 && (
                                    <div className="absolute bottom-1 left-1 text-[8px] uppercase tracking-[0.2em] text-meraki-bg bg-meraki-gold px-1.5 py-0.5">Cover</div>
                                )}
                            </div>
                            <input
                                value={img.caption || ""}
                                onChange={(e) => setCaption(i, e.target.value)}
                                placeholder="Add caption…"
                                maxLength={300}
                                data-testid={`${testid}-caption-${i}`}
                                className="w-full bg-meraki-surface border-t border-meraki-gold/20 px-2 py-1.5 text-[11px] text-meraki-text focus:outline-none focus:border-meraki-gold placeholder:text-meraki-muted/60"
                            />
                        </div>
                    ))}
                </div>
            )}
            {images.length < MAX_IMAGES && (
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onFiles}
                    data-testid={testid}
                    className="text-xs text-meraki-muted file:mr-4 file:py-2 file:px-4 file:border file:border-meraki-gold/30 file:bg-transparent file:text-meraki-gold file:uppercase file:tracking-wider file:text-[10px] hover:file:bg-meraki-gold/10"
                />
            )}
        </div>
    );
}

function GalleryForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial || empty);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (form.id) {
                const { id, created_at, ...payload } = form;
                await api.put(`/galleries/${id}`, payload);
                toast.success("Ride gallery updated");
            } else {
                await api.post("/galleries", form);
                toast.success("Ride gallery added");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="bg-meraki-surface border border-meraki-gold/20 p-6 md:p-8 space-y-6" data-testid="gallery-form">
            <h3 className="font-heading uppercase tracking-[0.25em] text-meraki-gold text-sm">
                {form.id ? "Edit ride gallery" : "Add new ride gallery"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <Field label="Ride Name">
                    <input data-testid="gallery-ride-name" required value={form.ride_name} onChange={(e) => set("ride_name", e.target.value)} className={inputCls} placeholder="e.g. Spiti Expedition '25" />
                </Field>
                <Field label="Location">
                    <input data-testid="gallery-location" value={form.location || ""} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder="e.g. Manali · Kaza · Chandratal" />
                </Field>
                <Field label="Total KMs">
                    <input data-testid="gallery-total-kms" value={form.total_kms || ""} onChange={(e) => set("total_kms", e.target.value)} className={inputCls} placeholder="e.g. 1200 km" />
                </Field>
                <Field label="Ride Date">
                    <input data-testid="gallery-ride-date" type="date" value={form.ride_date || ""} onChange={(e) => set("ride_date", e.target.value)} className={inputCls} />
                </Field>
            </div>

            <Field label="Photos (max 20)" hint="First photo becomes the folder cover. Each photo up to 8MB. You can select multiple at once.">
                <GalleryImagesEditor value={form.images} onChange={(v) => set("images", v)} testid="gallery-image-upload" />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Display Order" hint="Lower number = shown first">
                    <input data-testid="gallery-order" type="number" value={form.order ?? 0} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} className={inputCls} />
                </Field>
                <Field label="Visibility">
                    <select data-testid="gallery-active" value={form.active ? "true" : "false"} onChange={(e) => set("active", e.target.value === "true")} className={inputCls}>
                        <option value="true">Active (visible on site)</option>
                        <option value="false">Hidden</option>
                    </select>
                </Field>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-meraki-gold/15">
                <button data-testid="gallery-save-btn" type="submit" disabled={saving} className="px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50">
                    {saving ? "Saving…" : form.id ? "Update Gallery" : "Add Gallery"}
                </button>
                <button type="button" onClick={onCancel} className="px-6 py-3 border border-meraki-gold/30 text-meraki-text text-xs uppercase tracking-[0.25em] hover:border-meraki-gold transition-colors">
                    Cancel
                </button>
            </div>
        </form>
    );
}

export default function AdminGallery() {
    const [galleries, setGalleries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/galleries?include_inactive=true");
            setGalleries(data || []);
        } catch {
            toast.error("Failed to load galleries");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this ride gallery? This cannot be undone.")) return;
        try {
            await api.delete(`/galleries/${id}`);
            toast.success("Gallery removed");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">Meraki Riders Gallery</div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase text-meraki-text leading-tight">Ride Galleries</h1>
                    <p className="text-meraki-muted text-sm mt-3 max-w-xl">Create a folder for each ride — add the name, location and total KMs, then upload up to 20 photos.</p>
                </div>
                <button data-testid="add-gallery-btn" onClick={() => setEditing("new")} className="flex items-center gap-2 px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors">
                    <Plus className="w-4 h-4" /> Add Ride Gallery
                </button>
            </div>

            {editing && (
                <div className="mb-12">
                    <GalleryForm
                        initial={editing === "new" ? null : editing}
                        onCancel={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }}
                    />
                </div>
            )}

            {loading ? (
                <div className="text-meraki-muted text-sm">Loading…</div>
            ) : galleries.length === 0 ? (
                <div className="border border-dashed border-meraki-gold/20 p-12 text-center">
                    <Camera className="w-12 h-12 text-meraki-gold/40 mx-auto mb-4" />
                    <div className="text-meraki-muted text-sm">No ride galleries yet. Click <b className="text-meraki-text">Add Ride Gallery</b> to create your first folder.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="galleries-grid">
                    {galleries.map((gal) => (
                        <div key={gal.id} className="bg-meraki-surface border border-meraki-gold/20 overflow-hidden">
                            <div className="relative aspect-[4/3] bg-black">
                                {gal.images && gal.images[0] ? (
                                    <img src={gal.images[0].url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-meraki-gold/25"><ImageIcon className="w-12 h-12" /></div>
                                )}
                                <div className="absolute top-2 right-2 flex gap-2">
                                    <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 bg-black/70 ${gal.active ? "text-meraki-gold border border-meraki-gold/40" : "text-meraki-muted border border-meraki-muted/30"}`}>
                                        {gal.active ? "Active" : "Hidden"}
                                    </span>
                                    <span className="text-[9px] uppercase tracking-[0.2em] px-2 py-1 bg-black/70 text-meraki-gold border border-meraki-gold/40">{gal.images?.length || 0} pics</span>
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="font-display text-2xl uppercase text-meraki-text leading-none">{gal.ride_name}</div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                                    {gal.ride_date && <span className="inline-flex items-center gap-1.5 text-meraki-gold"><Calendar className="w-3 h-3" />{formatRideDate(gal.ride_date)}</span>}
                                    {gal.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-meraki-gold" />{gal.location}</span>}
                                    {gal.total_kms && <span className="inline-flex items-center gap-1.5 text-meraki-gold"><Gauge className="w-3 h-3" />{gal.total_kms}</span>}
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <button data-testid={`edit-gallery-${gal.id}`} onClick={() => setEditing(gal)} className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-meraki-gold/30 hover:border-meraki-gold text-meraki-gold text-[10px] uppercase tracking-[0.2em] transition-colors">
                                        <Edit2 className="w-3 h-3" /> Edit
                                    </button>
                                    <button data-testid={`delete-gallery-${gal.id}`} onClick={() => onDelete(gal.id)} className="inline-flex items-center justify-center gap-2 py-2 px-4 border border-red-900/40 hover:border-red-700 text-red-400 text-[10px] uppercase tracking-[0.2em] transition-colors">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AdminLayout>
    );
}
